// Ledger-style page shell: one top bar, one horizontal rule, no sidebar
// (admin/CLAUDE.md: "At 1366x768 a sidebar costs two visible line items.").
// Not the SignatureElement itself — that is workspace-specific (there is no
// single proforma number in the context of the queue list) — see
// pages/WorkspacePage.jsx for where the signature element actually renders.

import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import LanguageToggle from './LanguageToggle.jsx';
import AdminNav from './AdminNav.jsx';
import SalesNav from './SalesNav.jsx';
import CatalogManagerNav from './CatalogManagerNav.jsx';
import styles from './AppShell.module.css';

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.role === 'admin';
  const isSales = user?.role === 'sales';
  // Third staff role (migration 025) — see components/CatalogManagerNav.jsx's
  // own header comment. Mounted the same way AdminNav/SalesNav already are:
  // by role, never by one nav with a conditional inside it (admin/CLAUDE.md).
  const isCatalogManager = user?.role === 'catalog_manager';

  return (
    <div className={styles.shell}>
      <header className={styles.topBar}>
        <Link to="/" className={styles.brand}>
          {t('app.brand')}
          {/* Role-aware — a real bug, not a style choice: this used to be one
              generic key that always read "Sales pricing workspace," even
              under an admin session on the approval queue. See
              shared/i18n/strings.js's own comment on app.tagline.sales/admin.
              catalog_manager (migration 025) got its own tagline rather than
              falling back to either existing one — it is neither a sales
              workspace nor the full admin console. */}
          <span className={styles.tagline}>
            {t(
              isAdmin
                ? 'app.tagline.admin'
                : isCatalogManager
                  ? 'app.tagline.catalogManager'
                  : 'app.tagline.sales'
            )}
          </span>
        </Link>
        <div className={styles.topBarRight}>
          {user && <span className={styles.userName}>{user.full_name}</span>}
          <LanguageToggle />
          <button type="button" className={styles.logoutButton} onClick={() => logout()}>
            {t('nav.logout')}
          </button>
        </div>
      </header>
      <hr className="hairline" />
      {/* Each role sees only its own nav — AdminNav/SalesNav/
          CatalogManagerNav only ever mount under their matching session,
          never merely hidden by CSS for the others (admin/CLAUDE.md: "Role
          gating is enforced by the API, not by hiding UI"). A customer
          session (there is none in this app, but RequireRole elsewhere
          could theoretically reach here) gets no nav and the original
          single-<main>, no-sidebar layout. */}
      <div className={isAdmin || isSales || isCatalogManager ? styles.bodyWithNav : styles.body}>
        {isAdmin && <AdminNav />}
        {isSales && <SalesNav />}
        {isCatalogManager && <CatalogManagerNav />}
        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
}
